import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FactCheck } from '@mui/icons-material';
import { Alert, Box, Chip, Paper, Typography } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { LoadingState } from '../components/loading-state';
import { ReviewVerdictForm } from '../components/review-verdict-form';
import { evalResultsApi } from '../store/eval-results-api';
import { formatDateTime } from '../utils/formatting';

const { useGetV2AdminCortexEvalResultsByResultIdQuery: useGetEvalResult } = evalResultsApi;

const renderFieldValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value, null, 2);
};

const isMultilineValue = (value: unknown): boolean => {
  if (typeof value === 'string') return value.length > 80 || value.includes('\n');
  return typeof value === 'object' && value !== null;
};

const getFromListPath = (state: unknown): string | null => {
  if (typeof state !== 'object' || state === null || !('from' in state)) {
    return null;
  }

  const from = (state as { from?: unknown }).from;
  return typeof from === 'string' ? from : null;
};

export default function EvalResultDetail() {
  const { resultId } = useParams<{ resultId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isLoading, error, refetch } = useGetEvalResult(
    { resultId: resultId || '' },
    { skip: !resultId, refetchOnMountOrArgChange: true },
  );

  const result = data?.result;

  const explanation: Record<string, unknown> | undefined = result?.explanation;
  const explanationText =
    explanation && typeof explanation.text === 'string' ? explanation.text : null;

  const handleBack = () => {
    const fromList = getFromListPath(location.state);
    if (fromList) {
      navigate(fromList);
    } else {
      navigate('/admin-v2/eval-results');
    }
  };

  if (!resultId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert severity="error">Missing eval result ID in URL.</Alert>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading eval result..." />;
  }

  if (error || !result) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-4">
            <BackButton fallbackUrl="/admin-v2/eval-results" />
          </div>
          <Alert
            severity="error"
            action={
              <Typography
                component="button"
                variant="button"
                color="inherit"
                onClick={() => void refetch()}
                sx={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Retry
              </Typography>
            }
          >
            Eval result not found or failed to load.
          </Alert>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <button
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-900 text-sm font-medium inline-flex items-center"
          >
            ← Back
          </button>
        </div>

        {/* Header */}
        <Paper className="p-6 mb-6">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FactCheck fontSize="large" color="primary" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {result.scorer_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {result.feature} · {result.type_key}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip
              label={result.passed ? 'Pass' : 'Fail'}
              color={result.passed ? 'success' : 'error'}
              size="small"
            />
            <Chip
              label={result.applicable ? 'Applicable' : 'Not Applicable'}
              color={result.applicable ? 'default' : 'warning'}
              size="small"
              variant="outlined"
            />
            <Chip label={`scorer: ${result.scorer_kind}`} size="small" variant="outlined" />
            <Chip label={`source: ${result.source}`} size="small" variant="outlined" />
            {result.reviewed_at && (
              <Chip
                label={`reviewed ${formatDateTime(result.reviewed_at)}`}
                color="info"
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Eval Result ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {result.eval_result_id}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Encounter
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {result.encounter_id || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Patient
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {result.patient_id}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Inputs */}
        <Paper className="p-6 mb-6">
          <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2 }}>
            Inputs
          </Typography>
          {Object.keys(result.inputs).length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Object.entries(result.inputs).map(([key, value]) => {
                const multiline = isMultilineValue(value);
                return (
                  <Box key={key}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontFamily: 'monospace' }}
                    >
                      {key}
                    </Typography>
                    {multiline ? (
                      <Box
                        component="pre"
                        sx={{
                          mt: 0.5,
                          p: 1.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          fontSize: '0.8rem',
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          maxHeight: 360,
                          overflow: 'auto',
                        }}
                      >
                        {renderFieldValue(value)}
                      </Box>
                    ) : (
                      <Typography variant="body2">{renderFieldValue(value)}</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No inputs available.
            </Typography>
          )}
        </Paper>

        {/* Scorer Explanation */}
        <Paper className="p-6 mb-6">
          <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2 }}>
            Scorer Explanation
          </Typography>
          {explanationText ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {explanationText}
            </Typography>
          ) : explanation && Object.keys(explanation).length > 0 ? (
            <Box
              component="pre"
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              {JSON.stringify(explanation, null, 2)}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No explanation provided.
            </Typography>
          )}
        </Paper>

        {/* Review Form */}
        <ReviewVerdictForm
          resultId={result.eval_result_id}
          initialVerdict={result.human_verdict}
          initialNotes={result.human_notes}
          alreadyReviewed={Boolean(result.reviewed_at)}
        />
      </main>
    </div>
  );
}
